import { partition } from 'lodash-es'
import { type ChangeEvent, useCallback, useState } from 'react'

import { FvLinkButton, ValidatedForm } from '@fv/client-components'
import type { ClientAddressLookupResult, QuoteStopType } from '@fv/client-types'
import { fvDate } from '@fv/dates'

import {
  AddressInput,
  type AddressOption,
} from '@/components/inputs/AddressInput'
import {
  InputAdornment,
  InputGroup,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { ShipTypeSelector } from '@/components/inputs/ShipTypeSelector'
import type { FormSectionProps } from '@/components/inputs/types'
import { PillHeader } from '@/components/PillHeader'
import { RouteStopDate } from '@/components/QuoteRequest/RouteStopDate'
import { getMapUrl } from '@/utils/getMapUrl'

import { AddressBookButton } from '../addresses/AddressBookButton'
import { addressMapper } from '../addresses/addressFuncs'
import { LocationAccessorialForm } from '../shipment-location-form/LocationAccessorialForm'
import type { ShipmentLocationFormModel } from '../shipment-location-form/types'
import { useQuotePageCtx, useQuotePageWorkflow } from './QuotePageProvider'

const dateFormat = 'ddd MMM D'

type Props = FormSectionProps & {
  onSubmit?: () => void
}
export const QuoteFormLocations = ({ disabled, onSubmit }: Props) => {
  const [isPickingDate, setPickingDate] = useState(false)
  const actions = useQuotePageCtx(s => s.actions)
  const workflow = useQuotePageWorkflow()
  const isLtl = workflow === 'ltl'
  const isParcel = workflow === 'parcel'
  const load = useQuotePageCtx(s => s.load)
  const locations = useQuotePageCtx(s => s.locations)
  const [origin, destination] = locations

  const formRef = useCallback(
    (ref: HTMLFormElement | null) => actions.setFormRef(ref),
    [actions],
  )

  const organizeAddressOptions = (locs: AddressOption[]) => {
    const takeAddresses = 20
    const [addresses, cities] = partition(locs, loc => !!loc.value._id)
    return [...addresses.slice(0, takeAddresses), ...cities]
  }

  function getInputProps(
    stopType: QuoteStopType,
    name: keyof ShipmentLocationFormModel,
  ) {
    const stop = stopType === 'origin' ? origin : destination

    return {
      disabled,
      name: `${stopType}.${name}`,
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        actions.setLocationData(stopType, { [name]: e.target.value })
      },
      value: stop[name] as string,
    }
  }

  function setAddress(stopType: QuoteStopType) {
    return (location: ClientAddressLookupResult | null) => {
      if (disabled) return
      const data = addressMapper.dtoToFormStop(location, workflow)
      actions.setLocationData(stopType, data)
    }
  }

  return (
    <div className="b900:mb-12 flex flex-col basis-1/2 relative ">
      <ValidatedForm
        autoReportNextError={false}
        className="flex w-auto flex-1 flex-col"
        ref={formRef}
        onValidSubmit={onSubmit}
      >
        <PillHeader className="beer-container !border-2">
          Pickup and delivery
        </PillHeader>

        <div className="flex flex-wrap gap-x-2 px-4 pt-7 bg-fv-orange-25 border-fv-orange border">
          <InputGroupWrapper
            className="basis-full"
            label="Pick up from origin"
            name="origin"
            required
          >
            <InputAdornment position="start">
              <FvLinkButton
                disabled={!origin.postalCode}
                href={getMapUrl(origin)}
                className="!bg-fv-orange-50"
                icon="map-marker"
                iconClass="text-fv-orange"
                rel="noreferrer"
                target="_blank"
              />
            </InputAdornment>

            <AddressBookButton
              config={{ context: workflow, type: 'shipping' }}
              disabled={disabled}
              setAddress={setAddress('origin')}
            />

            <AddressInput
              context={workflow}
              includeAddressBook
              location={origin}
              filterOptions={organizeAddressOptions}
              name="origin"
              onLocationChange={setAddress('origin')}
              placeholder="Company, zip or postal code"
              required
            />
          </InputGroupWrapper>

          {isParcel && (
            <>
              <LocationAccessorialForm
                value={origin}
                disabled={disabled}
                workflow={load.workflow}
                onChange={value => actions.setLocationData('origin', value)}
              />

              <InputGroup
                className="basis-full"
                inputProps={{
                  name: 'schedulePickup',
                  onChange: actions.handleLoadChange,
                  value: `${load.schedulePickup ?? false}`,
                  options: [
                    { label: 'Daily Pickup', value: 'false' },
                    {
                      label: 'Schedule Pickup',
                      value: 'true',
                      disabled: load.isFreightCollect,
                    },
                  ],
                }}
                inputType="radio"
                label="Pickup Type"
              />
            </>
          )}

          <InputGroup
            className="basis-4/12"
            inputProps={{
              ...getInputProps('origin', 'stopDate'),
              onChange: undefined,
              onFocus: () => setPickingDate(true),
              readOnly: true,
              value: origin.stopDate
                ? fvDate(origin.stopDate).format(dateFormat)
                : '',
            }}
            inputType="text"
            label="Pick up on"
            required
            startContent={
              <InputAdornment position="start" icon="calendar-day" />
            }
          />

          <InputGroup
            className="flex-1"
            inputProps={getInputProps('origin', 'refNum')}
            inputType="text"
            label="Shipper's #"
            startContent={
              <InputAdornment
                icon="hashtag"
                iconClass="text-fv-gray-dark"
                position="start"
              />
            }
          />

          {isLtl && (
            <ShipTypeSelector
              className="basis-full"
              value={origin}
              onChange={({ shipType, accessorials }) => {
                actions.setLocationData('origin', { shipType, accessorials })
              }}
              name="origin-shipType"
              label="Pickup location type"
            />
          )}

          {isLtl && (
            <LocationAccessorialForm
              value={origin}
              disabled={disabled}
              workflow={load.workflow}
              onChange={value => actions.setLocationData('origin', value)}
            />
          )}
        </div>

        <div className="flex flex-wrap bg-fv-green-50 content-start flex-1 p-4 mt-4 border border-fv-green">
          <InputGroupWrapper
            className="basis-full"
            label="Deliver to destination"
            name="destination"
            required
          >
            <InputAdornment position="start">
              <FvLinkButton
                disabled={!destination.postalCode}
                className="!bg-fv-green-50"
                href={getMapUrl(destination)}
                icon="map-marker"
                iconClass="text-fv-green"
                rel="noreferrer"
                target="_blank"
              />
            </InputAdornment>

            <AddressBookButton
              config={{ context: workflow, type: 'shipping' }}
              disabled={disabled}
              setAddress={setAddress('destination')}
            />

            <AddressInput
              context={workflow}
              includeAddressBook
              location={destination}
              filterOptions={organizeAddressOptions}
              name="destination"
              onLocationChange={setAddress('destination')}
              placeholder="Company, zip or postal code"
              required
            />
          </InputGroupWrapper>

          {isLtl && (
            <ShipTypeSelector
              className="basis-full"
              value={destination}
              onChange={({ shipType, accessorials }) => {
                actions.setLocationData('destination', {
                  shipType,
                  accessorials,
                })
              }}
              name="destination-shipType"
              label="Delivery location type"
            />
          )}
          <div className="w-full">
            <LocationAccessorialForm
              workflow={load.workflow}
              value={destination}
              onChange={value => actions.setLocationData('destination', value)}
            />
          </div>
        </div>
        <button type="submit" className="hidden" />
      </ValidatedForm>

      <RouteStopDate
        onClose={() => setPickingDate(false)}
        isOpen={isPickingDate}
        sequence={0}
        onChange={data => {
          actions.setLocationData('origin', data)
        }}
        showStopDetailConfig={false}
        stops={locations}
      />
    </div>
  )
}
